import { useCallback, useEffect, useState } from "react"
import { Link, Navigate, useParams } from "react-router-dom"
import { isAxiosError } from "axios"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import { ConfirmDialog } from "../../shared/ui/ConfirmDialog"
import {
  listAdminReviewsApi,
  deleteAdminReviewApi,
  AdminReviewItem,
  ReviewType,
} from "../../features/reviews/api/adminReviews.api"
import { REVIEW_TYPE_MAP, ReviewTypeMeta, isReviewType } from "./reviewTypes"
import {
  StarIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  ChatBubbleLeftEllipsisIcon,
  LinkSlashIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
} from "@heroicons/react/16/solid"

const LIMIT = 20
const COMMENT_TRUNCATE = 70

const formatDate = (unixSeconds: number) =>
  new Date(unixSeconds * 1000).toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })

const Stars = ({ rating }: { rating: number }) => (
  <span className="inline-flex items-center gap-1">
    <span className="inline-flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <StarIcon key={i} className={`w-3.5 h-3.5 ${i < Math.round(rating) ? "text-amber-400" : "text-gray-200"}`} />
      ))}
    </span>
    <span className="text-xs text-gray-400 font-medium">{rating}/5</span>
  </span>
)

const CopyableId = ({ value }: { value: string }) => {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // clipboard unavailable — ignore
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      title="Скопировать"
      className="inline-flex items-center gap-1 font-mono text-xs text-gray-500 hover:text-gray-800 transition-colors group/copy"
    >
      {value.slice(0, 10)}…
      {copied
        ? <CheckIcon className="w-3 h-3 text-emerald-500 flex-shrink-0" />
        : <ClipboardDocumentIcon className="w-3 h-3 text-gray-300 group-hover/copy:text-gray-500 flex-shrink-0" />
      }
    </button>
  )
}

const CommentCell = ({ comment }: { comment: string }) => {
  const [expanded, setExpanded] = useState(false)

  if (!comment) return <span className="text-gray-300">—</span>

  const needsTruncate = comment.length > COMMENT_TRUNCATE

  if (!needsTruncate) {
    return <p className="text-gray-700 max-w-[280px]">{comment}</p>
  }

  return (
    <button
      type="button"
      onClick={() => setExpanded((e) => !e)}
      title={expanded ? "Свернуть" : comment}
      className="text-left text-gray-700 hover:text-gray-900 transition-colors"
    >
      <p className={expanded ? "max-w-[280px]" : "max-w-[280px] truncate"}>
        {expanded ? comment : `${comment.slice(0, COMMENT_TRUNCATE)}…`}
      </p>
      <span className="text-[11px] text-blue-500 font-medium">{expanded ? "свернуть" : "показать целиком"}</span>
    </button>
  )
}

const TargetCell = ({ item, meta }: { item: AdminReviewItem; meta: ReviewTypeMeta }) => {
  if (!item.target_name) {
    return (
      <div className="flex items-center gap-1.5">
        <LinkSlashIcon className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" title="Объект не резолвится (возможно, удалён)" />
        <span className="font-mono text-xs text-gray-400">{item.target_id.slice(0, 14)}…</span>
      </div>
    )
  }
  return (
    <Link
      to={meta.targetLink(item.target_id)}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm text-gray-800 hover:text-blue-600 transition-colors group/target"
    >
      <span className="truncate max-w-[180px]">{item.target_name}</span>
      <ArrowTopRightOnSquareIcon className="w-3 h-3 text-gray-300 group-hover/target:text-blue-500 flex-shrink-0" />
    </Link>
  )
}

type Toast = { text: string; variant: "success" | "error" }

export const AdminReviewsPage = () => {
  const { type } = useParams<{ type: string }>()

  if (!type || !isReviewType(type)) {
    return <Navigate to="/admin/reviews/course" replace />
  }

  return <AdminReviewsPageInner key={type} type={type} />
}

const AdminReviewsPageInner = ({ type }: { type: ReviewType }) => {
  const [targetId, setTargetId] = useState("")
  const [pendingTargetId, setPendingTargetId] = useState("")
  const [page, setPage] = useState(1)

  const [items, setItems] = useState<AdminReviewItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const [pendingDelete, setPendingDelete] = useState<AdminReviewItem | null>(null)
  const [deleting, setDeleting] = useState(false)

  const [toast, setToast] = useState<Toast | null>(null)
  const showToast = (text: string, variant: Toast["variant"] = "success") => {
    setToast({ text, variant })
    setTimeout(() => setToast((t) => (t?.text === text ? null : t)), 4000)
  }

  const load = useCallback(async () => {
    setLoading(true)
    setError(false)
    try {
      const offset = (page - 1) * LIMIT
      const res = await listAdminReviewsApi({
        type,
        limit: LIMIT,
        offset,
        ...(targetId ? { target_id: targetId } : {}),
      })
      setItems(res.data.items)
      setTotal(res.data.total)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [type, targetId, page])

  useEffect(() => { load() }, [load])

  const applyTargetFilter = () => {
    setTargetId(pendingTargetId.trim())
    setPage(1)
  }

  const clearTargetFilter = () => {
    setTargetId("")
    setPendingTargetId("")
    setPage(1)
  }

  const handleDelete = async () => {
    if (!pendingDelete) return
    setDeleting(true)
    try {
      await deleteAdminReviewApi(pendingDelete.type, pendingDelete.id)
      setItems((prev) => prev.filter((i) => i.id !== pendingDelete.id))
      setTotal((t) => Math.max(0, t - 1))
      showToast("Отзыв удалён")
      setPendingDelete(null)
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        showToast("Отзыв не найден, возможно уже удалён", "error")
        setItems((prev) => prev.filter((i) => i.id !== pendingDelete.id))
        setPendingDelete(null)
      } else {
        showToast("Не удалось удалить отзыв", "error")
      }
    } finally {
      setDeleting(false)
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / LIMIT))
  const activeMeta = REVIEW_TYPE_MAP[type]

  return (
    <DashboardLayout title="Отзывы">

      {/* ── Breadcrumb / heading ── */}
      <div className="flex items-center gap-3 mb-5">
        <Link
          to="/admin/reviews"
          className="w-8 h-8 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center flex-shrink-0 transition-colors"
        >
          <ChevronLeftIcon className="w-4 h-4 text-gray-500" />
        </Link>
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${activeMeta.color}`}>
          {activeMeta.icon}
        </span>
        <div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <Link to="/admin/reviews" className="hover:text-gray-600 transition-colors">Отзывы</Link>
            <span>/</span>
            <span className="text-gray-500">{activeMeta.label}</span>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 leading-tight">{activeMeta.label}</h2>
        </div>
      </div>

      <div className="bg-white rounded-xl border overflow-hidden">

        {/* ── Filter bar ── */}
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b bg-gray-50/60">
          <div className="flex flex-1 min-w-[240px] max-w-sm items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 shrink-0" />
            <input
              type="text"
              placeholder="ID объекта…"
              value={pendingTargetId}
              onChange={(e) => setPendingTargetId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyTargetFilter()}
              className="flex-1 text-sm outline-none font-mono"
            />
          </div>

          <button
            onClick={applyTargetFilter}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition"
          >
            Найти
          </button>

          {targetId && (
            <button
              onClick={clearTargetFilter}
              className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2 transition-colors"
            >
              Сбросить фильтр
            </button>
          )}

          <span className="ml-auto text-xs text-gray-400">
            {!loading && `${total} отзывов`}
          </span>
        </div>

        {/* ── Table ── */}
        {error ? (
          <div className="py-16 text-center">
            <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400 mb-2">Не удалось загрузить отзывы</p>
            <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
              Повторить
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/40">
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Оценка</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Комментарий</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Теги</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Объект</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">ID пользователя</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Дата создания</th>
                  <th className="px-5 py-3 w-16" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      {[1, 2, 3, 4, 5, 6, 7].map((j) => (
                        <td key={j} className="px-5 py-4">
                          <div className="h-4 bg-gray-100 rounded animate-pulse w-20" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-16 text-center">
                      <ChatBubbleLeftEllipsisIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                      <p className="text-sm text-gray-400">Отзывов нет</p>
                    </td>
                  </tr>
                ) : (
                  items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50/60 transition-colors group align-top">
                      <td className="px-5 py-3.5"><Stars rating={item.rating} /></td>
                      <td className="px-5 py-3.5"><CommentCell comment={item.comment} /></td>
                      <td className="px-5 py-3.5">
                        <div className="flex flex-wrap gap-1 max-w-[160px]">
                          {item.tags.length > 0
                            ? item.tags.map((tag) => (
                                <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs whitespace-nowrap">{tag}</span>
                              ))
                            : <span className="text-gray-300">—</span>}
                        </div>
                      </td>
                      <td className="px-5 py-3.5"><TargetCell item={item} meta={activeMeta} /></td>
                      <td className="px-5 py-3.5"><CopyableId value={item.user_id} /></td>
                      <td className="px-5 py-3.5 text-xs text-gray-400 whitespace-nowrap">{formatDate(item.created_at)}</td>
                      <td className="px-5 py-3.5">
                        <button
                          onClick={() => setPendingDelete(item)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-red-600 transition-colors px-2 py-1.5 rounded-lg hover:bg-red-50 opacity-0 group-hover:opacity-100"
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && !error && total > 0 && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t bg-gray-50/40">
            <span className="text-xs text-gray-400">
              Страница <span className="font-medium text-gray-600">{page}</span> из{" "}
              <span className="font-medium text-gray-600">{totalPages}</span>
            </span>
            <div className="flex gap-1.5">
              <button
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ← Назад
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-xs font-medium border rounded-lg text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Вперёд →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Delete confirm ── */}
      <ConfirmDialog
        open={!!pendingDelete}
        title="Удалить отзыв?"
        message="Действие нельзя отменить."
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />

      {/* ── Toast ── */}
      {toast && (
        <div
          className={`fixed top-5 right-5 z-[60] flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium shadow-lg ${
            toast.variant === "success" ? "bg-emerald-600 text-white" : "bg-red-600 text-white"
          }`}
        >
          {toast.variant === "success"
            ? <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
            : <XCircleIcon className="w-4 h-4 flex-shrink-0" />
          }
          {toast.text}
        </div>
      )}
    </DashboardLayout>
  )
}
